import { useState } from 'react'
import { usePendingUsers, useApproveUser, useRejectUser, type AdminUser } from '../lib/admin'
import { useToast } from '../components/ToastContainer'
import Modal from '../components/Modal'

export default function AdminPending() {
  const { data: pending, isLoading, error } = usePendingUsers()
  const approveMutation = useApproveUser()
  const rejectMutation = useRejectUser()
  const { success: toastSuccess, error: toastError } = useToast()

  const [rejectTarget, setRejectTarget] = useState<AdminUser | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [confirmingReject, setConfirmingReject] = useState(false)

  const handleApprove = async (userId: string) => {
    if (!window.confirm('Tem certeza que deseja aprovar este usuário?')) return
    try {
      await approveMutation.mutateAsync(userId)
      toastSuccess('Usuário aprovado com sucesso!')
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 401 || status === 403) {
        toastError('Você não tem permissão para aprovar usuários. Faça login como administrador.')
      } else if (status === 400) {
        toastError(err?.response?.data?.detail ?? 'Usuário não está mais pendente de aprovação.')
      } else if (status === 404) {
        toastError('Usuário não encontrado.')
      } else {
        toastError(err?.response?.data?.detail ?? 'Erro ao aprovar usuário. Tente novamente.')
      }
    }
  }

  const openReject = (user: AdminUser) => {
    setRejectTarget(user)
    setRejectReason('')
    setConfirmingReject(true)
  }

  const closeReject = () => {
    setRejectTarget(null)
    setRejectReason('')
    setConfirmingReject(false)
  }

  const handleReject = async () => {
    if (!rejectTarget) return
    try {
      await rejectMutation.mutateAsync({ userId: rejectTarget.id, reason: rejectReason })
      toastSuccess('Usuário rejeitado com sucesso!')
      closeReject()
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 401 || status === 403) {
        toastError('Você não tem permissão para rejeitar usuários. Faça login como administrador.')
      } else if (status === 400) {
        toastError(err?.response?.data?.detail ?? 'Usuário não está mais pendente de aprovação.')
      } else if (status === 404) {
        toastError('Usuário não encontrado.')
      } else {
        toastError(err?.response?.data?.detail ?? 'Erro ao rejeitar usuário. Tente novamente.')
      }
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[var(--color-brand-200)] border-t-[var(--color-brand-500)]" />
      </div>
    )
  }

  if (error) {
    return <p className="text-[var(--color-danger)]">Erro ao carregar usuários pendentes</p>
  }

  const users = pending ?? []

  if (users.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-[var(--color-text-muted)]">Nenhum usuário pendente</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-[var(--color-text)]">Usuários Pendentes</h2>
      <div className="divide-y divide-[var(--color-border)]">
        {users.map((user) => (
          <div key={user.id} className="flex items-center justify-between p-4">
            <div>
              <h3 className="text-sm font-medium text-[var(--color-text)]">
                {user.full_name || 'Sem nome'}
              </h3>
              <p className="text-sm text-[var(--color-text-muted)]">{user.email}</p>
              <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                Criado em {new Date(user.created_at).toLocaleDateString('pt-BR')}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleApprove(user.id)}
                disabled={approveMutation.isPending}
                className="rounded-lg bg-[var(--color-success-500)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--color-success-600)] disabled:opacity-50"
              >
                {approveMutation.isPending ? 'Aprovando...' : 'Aprovar'}
              </button>
              <button
                onClick={() => openReject(user)}
                disabled={rejectMutation.isPending}
                className="rounded-lg bg-[var(--color-danger-500)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--color-danger-600)] disabled:opacity-50"
              >
                Rejeitar
              </button>
            </div>
          </div>
        ))}
      </div>

      {confirmingReject && rejectTarget && (
        <Modal isOpen={true} onClose={closeReject} title="Rejeitar usuário">
          <div className="space-y-4">
            <p className="text-sm text-[var(--color-text-muted)]">
              Tem certeza que deseja rejeitar <strong>{rejectTarget.full_name || rejectTarget.email}</strong>?
            </p>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)]">
                Motivo (opcional)
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                placeholder="Ex: Dados incorretos, duplicata, outra razão..."
                className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] placeholder-[var(--color-text-muted)] focus:border-[var(--color-brand-500)] focus:outline-none"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeReject}
                className="rounded-lg border border-[var(--color-border)] bg-white px-4 py-2 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-subtle)]"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={rejectMutation.isPending}
                className="rounded-lg bg-[var(--color-danger-500)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-danger-600)] disabled:opacity-50"
              >
                {rejectMutation.isPending ? 'Rejeitando...' : 'Rejeitar'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
